<template>
  <div class="login-background">
    <div class="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div class="sm:mx-auto sm:w-full sm:max-w-md">
        <h2 class="mt-6 text-center text-2xl font-bold leading-9 tracking-tight text-white">
          Reset Password
        </h2>
      </div>
      <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
        <div class="bg-white px-6 py-12 shadow sm:rounded-mc-md sm:px-12">

          <!-- Form state -->
          <div v-if="pageState === 'form'" data-testid="form-state">
            <h3 class="text-lg font-bold text-mc-ink mb-1">Set New Password</h3>
            <p class="text-sm text-ink-muted mb-5">Choose a strong password for your account.</p>
            <form @submit.prevent="submitForm" class="space-y-4">
              <div>
                <label for="new-password" class="block text-xs font-semibold text-mc-ink mb-1">New password</label>
                <input
                  id="new-password"
                  data-testid="new-password"
                  type="password"
                  autocomplete="new-password"
                  v-model="newPassword"
                  class="block w-full rounded-mc-sm border border-mc-steel bg-mc-white py-1.5 px-3 text-sm text-mc-ink focus:outline-none focus:ring-2 focus:ring-mc-blue/40"
                />
              </div>
              <div>
                <label for="confirm-password" class="block text-xs font-semibold text-mc-ink mb-1">Confirm password</label>
                <input
                  id="confirm-password"
                  data-testid="confirm-password"
                  type="password"
                  autocomplete="new-password"
                  v-model="confirmPassword"
                  class="block w-full rounded-mc-sm border border-mc-steel bg-mc-white py-1.5 px-3 text-sm text-mc-ink focus:outline-none focus:ring-2 focus:ring-mc-blue/40"
                />
              </div>
              <p v-if="fieldError" data-testid="field-error" role="alert" class="text-sm font-semibold text-mc-red">{{ fieldError }}</p>
              <button
                data-testid="submit-btn"
                type="submit"
                :disabled="isLoading"
                class="w-full inline-flex justify-center items-center rounded-pill border-2 border-mc-red bg-mc-red px-4 py-2 text-sm font-bold text-white transition-colors duration-160 hover:bg-mc-red-700 hover:border-mc-red-700 disabled:opacity-60"
              >
                {{ isLoading ? 'Setting password...' : 'Set password' }}
              </button>
              <button
                type="button"
                @click="goToLogin"
                class="w-full inline-flex justify-center items-center rounded-pill border-2 border-mc-ink bg-transparent px-4 py-2 text-sm font-bold text-mc-ink transition-colors duration-160 hover:bg-mc-ink hover:text-white"
              >
                Back to login
              </button>
            </form>
          </div>

          <!-- Success state -->
          <div v-else-if="pageState === 'success'" data-testid="success-state" class="text-center">
            <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-mc-red-100">
              <svg aria-hidden="true" class="h-6 w-6 text-mc-red" fill="none" stroke="currentColor" stroke-width="2.5"
                   viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h3 class="text-lg font-bold text-mc-ink mb-1">Password updated</h3>
            <p class="text-sm text-ink-muted mb-5">Redirecting you to login...</p>
            <div class="h-1.5 w-full overflow-hidden rounded-pill bg-mc-cloud">
              <div class="h-full rounded-pill bg-mc-red transition-all duration-[3000ms] ease-linear"
                   :style="{ width: progressWidth }"></div>
            </div>
            <p data-testid="countdown" class="mt-2 text-xs text-ink-muted">
              Taking you to login in {{ countdown }}s
            </p>
          </div>

          <!-- Error state -->
          <div v-else data-testid="error-state" class="text-center">
            <div class="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-mc-red-100">
              <svg aria-hidden="true" class="h-6 w-6 text-mc-red" fill="none" stroke="currentColor" stroke-width="2.5"
                   viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </div>
            <h3 data-testid="error-heading" class="text-lg font-bold text-mc-ink mb-1">{{ errorHeading }}</h3>
            <p data-testid="error-message" class="text-sm text-ink-muted mb-5">{{ errorMessage }}</p>
            <button
              data-testid="resend-btn"
              type="button"
              @click="goToReset"
              class="w-full inline-flex justify-center items-center rounded-pill border-2 border-mc-red bg-mc-red px-4 py-2 text-sm font-bold text-white transition-colors duration-160 hover:bg-mc-red-700 hover:border-mc-red-700 mb-3"
            >
              Request a new reset email
            </button>
            <button
              data-testid="back-to-login-btn"
              type="button"
              @click="goToLogin"
              class="w-full inline-flex justify-center items-center rounded-pill border-2 border-mc-ink bg-transparent px-4 py-2 text-sm font-bold text-mc-ink transition-colors duration-160 hover:bg-mc-ink hover:text-white"
            >
              Back to login
            </button>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { confirmPasswordReset } from 'firebase/auth';
import { auth } from '@/firebase';

export default {
  name: 'ConfirmResetPassword',
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { mode, oobCode } = route.query;

    const pageState = ref('form');
    const newPassword = ref('');
    const confirmPassword = ref('');
    const fieldError = ref('');
    const isLoading = ref(false);
    const errorHeading = ref('');
    const errorMessage = ref('');
    const countdown = ref(3);
    const progressWidth = ref('0%');
    let countdownInterval = null;

    onMounted(() => {
      if (mode !== 'resetPassword' || !oobCode) {
        errorHeading.value = 'Link expired';
        errorMessage.value = 'This password reset link has expired or has already been used.';
        pageState.value = 'error';
      }
    });

    onUnmounted(() => {
      if (countdownInterval) clearInterval(countdownInterval);
    });

    const startCountdown = () => {
      progressWidth.value = '100%';
      countdownInterval = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownInterval);
          router.push({ name: 'Login' });
        }
      }, 1000);
    };

    const submitForm = async () => {
      fieldError.value = '';
      if (newPassword.value !== confirmPassword.value) {
        fieldError.value = 'Passwords do not match';
        return;
      }
      isLoading.value = true;
      try {
        await confirmPasswordReset(auth, oobCode, newPassword.value);
        pageState.value = 'success';
        startCountdown();
      } catch (err) {
        if (err.code === 'auth/weak-password') {
          fieldError.value = 'Password must be at least 6 characters';
        } else if (err.code === 'auth/expired-action-code' || err.code === 'auth/invalid-action-code') {
          errorHeading.value = 'Link expired';
          errorMessage.value = 'This password reset link has expired or has already been used.';
          pageState.value = 'error';
        } else {
          errorHeading.value = 'Something went wrong';
          errorMessage.value = 'An unexpected error occurred. Please try again.';
          pageState.value = 'error';
        }
      } finally {
        isLoading.value = false;
      }
    };

    const goToLogin = () => router.push({ name: 'Login' });
    const goToReset = () => router.push({ name: 'ResetPassword' });

    return {
      pageState,
      newPassword,
      confirmPassword,
      fieldError,
      isLoading,
      errorHeading,
      errorMessage,
      countdown,
      progressWidth,
      submitForm,
      goToLogin,
      goToReset,
    };
  },
};
</script>

<style>
.login-background {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 98vh;
  background-image: url('@/assets/images/MeshComm-Hero-Area-SIGNAL.jpg');
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}
</style>
